'use client';

import styles from './modal.module.css';
import React, { useCallback, useEffect, useMemo } from 'react';
import { cn } from '../../utils/cn';
import type { ModalProps } from './modal.types';
import Button from '../../atoms/button';
import Text from '../../atoms/text';
import Icon from '../../atoms/icon';

export const Modal: React.FC<ModalProps> = ({
    open,
    onClose,
    title,
    titleProps,
    size = 'medium',
    closeOnOverlayClick = true,
    showCloseButton = true,
    children,
    footer,
    header,
    className,
    ...props
}) => {
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && open) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open, onClose]);

    const onClick = useCallback(() => closeOnOverlayClick && onClose(), [closeOnOverlayClick, onClose]);

    const renderTitle = useMemo(() => {
        return (
            (title || showCloseButton) && (
                <div className={styles.header}>
                    <Text
                        showIf={!!title}
                        {...titleProps}
                        variant="h2"
                        className={cn(styles.title, titleProps?.className)}
                    >
                        {title}
                    </Text>
                    {showCloseButton && (
                        <Icon
                            aria-label="Close modal"
                            data-testid={'icon-button'}
                            className={styles.icon}
                            onClick={onClose}
                            name="x"
                        />
                    )}
                </div>
            )
        );
    }, [onClose, showCloseButton, title, titleProps]);

    if (!open) {
        return null;
    }

    return (
        <div className={styles.container}>
            {/* Overlay */}
            <Button className={styles.backdrop} onClick={onClick} />

            {/* Modal */}
            <div
                className={cn(styles.modal, styles[`modal--${size}`], className)}
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                {...props}
            >
                {/* Header */}
                {header ?? renderTitle}

                {/* Content - Scrollable */}
                <div className={styles.content}>{children}</div>

                {/* Footer */}
                {footer && <div>{footer}</div>}
            </div>
        </div>
    );
};
